import { useCallback, useEffect, useMemo, useState } from "react";

import { addYears, format } from "date-fns";
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2,
  XIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { useAuth } from "@/components/lib/auth";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { CompletionRequirements } from "@/components/pieces/roleplays/CompletionRequirements";
import { DifficultyTag } from "@/components/pieces/roleplays/DifficultyTag";
import { PeopleAndGroupInput } from "@/components/pieces/roleplays/PeopleAndGroupInput";
import { Scenario } from "@/components/pieces/roleplays/ScenarioCard";
import { AvatarWithPrettyNull } from "@/components/pieces/users/AvatarWithPrettyNull";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SharingStatusIcon } from "@/components/ui/sharing/SharingStatusIcon";
import { Textarea } from "@/components/ui/textarea";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  RoleplayRank,
  ScenarioAssignmentDisplayStatus,
  ScenarioAssignmentsDocument,
  ScenarioAssignmentsQuery,
  ScenarioAssignmentsStatisticDocument,
  useBulkAssignScenariosMutation,
  useScenarioAssignmentsQuery,
  useScenariosListQuery,
} from "@/graphql";
import { getBrowserTimezone } from "@/utils/datetime";

interface AssignScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId?: string; // New prop to determine if scenario selection is needed
  scenarioId?: string; // Single scenario to assign
  scenarios?: Scenario[]; // Available scenarios for multi-select
  scenarioSlug?: string;
  onSuccess?: () => void;
}

interface SelectedTag {
  id: string;
  name: string;
  type: "person" | "group";
}

interface AssignScenarioFormData {
  selectedUsersAndGroups: SelectedTag[];
  searchValue: string;
  setDueDate: boolean;
  dueDate?: Date;
  timezone: string;
  includeCustomMessage: boolean;
  customMessage: string;
  selectedScenarios?: string[]; // New field for scenario selection
  scenarioAttemptMin?: number;
  scenarioAttemptMax?: number;
  scenarioRankMin?: RoleplayRank;
}

type Assignment = NonNullable<
  NonNullable<
    NonNullable<ScenarioAssignmentsQuery["scenarioAssignments"]>["data"]
  >[0]
>;

function AssignmentCard({ assignment }: { assignment: Assignment | null }) {
  if (!assignment) return null;
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex flex-col">
          <span className="font-semibold text-sm text-gray-900">
            {assignment.assignee?.name}
          </span>
          <span className="text-sm text-gray-500">
            {assignment.assignee?.email}
          </span>
        </div>
      </div>
      <div className="text-right shrink-0">
        {assignment.displayStatus ===
          ScenarioAssignmentDisplayStatus.NotStarted &&
          assignment.dueDate && (
            <span className="text-sm text-gray-500">
              Due {format(new Date(assignment.dueDate), "MM/dd/yyyy")}
            </span>
          )}
        {assignment.displayStatus === ScenarioAssignmentDisplayStatus.PastDue &&
          assignment.dueDate && (
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 rounded-full bg-amber-500 flex items-center justify-center">
                <XIcon className="w-2 h-2 text-white" />
              </div>
              <span className="text-sm text-amber-600">
                Past Due {format(new Date(assignment.dueDate), "MM/dd/yyyy")}
              </span>
            </div>
          )}
      </div>
    </div>
  );
}

export function SimpleScenarioCard({
  scenario,
  selected,
  onToggle,
  selectable = true,
  avatarSize = 60,
  showShortDesc = true,
}: {
  scenario: Scenario;
  selected: boolean;
  onToggle: () => void;
  selectable?: boolean;
  avatarSize?: number;
  showShortDesc?: boolean;
}) {
  // Simplified - just use onToggle directly without complex callbacks
  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent double firing if clicking on checkbox
    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    onToggle();
  };

  return (
    <div
      className={`flex gap-3 p-3 border bg-white rounded-lg cursor-pointer transition-colors ${
        selected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:border-gray-300"
      }`}
      onClick={handleCardClick}
    >
      {selectable && (
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onCheckedChange={onToggle} />
        </div>
      )}

      <AvatarWithPrettyNull
        user={{
          name: scenario.personaName,
          photo: scenario.personaPhotoUrl,
        }}
        size={avatarSize}
        data-test-id="scenario-image"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center mb-2 justify-between">
          <div className="flex flex-col">
            <div className="font-semibold text-sm text-gray-900">
              {scenario.name}
            </div>
            {showShortDesc && scenario.shortDesc && (
              <div className="text-sm text-gray-500 mt-1">
                {scenario.shortDesc}
              </div>
            )}
          </div>
          <div className="flex items-center justify-center h-6 w-6 mt-2">
            {scenario.sharingStatus && (
              <SharingStatusIcon
                sharingStatus={scenario.sharingStatus}
                size={14}
              />
            )}
          </div>
        </div>

        <DifficultyTag difficulty={scenario.difficulty} />
      </div>
    </div>
  );
}

enum Step {
  SELECT_PEOPLE_AND_GROUPS = 0,
  CONFIG_ASSIGNMENT = 1,
  CHOOSE_SCENARIO = 2,
}

export function AssignScenarioDialog({
  open,
  onOpenChange,
  collectionId,
  scenarioId,
  scenarioSlug,
}: AssignScenarioDialogProps) {
  const { currentWorkspace } = useAuth();
  const [step, setStep] = useState(Step.SELECT_PEOPLE_AND_GROUPS);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>(
    scenarioId ? [scenarioId] : [],
  );
  const [includeCustomMessage, setIncludeCustomMessage] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { data: scenariosData } = useScenariosListQuery({
    variables: {
      collectionIds: collectionId ? [collectionId] : undefined,
      page,
      pageSize: 4,
    },
    skip: !collectionId,
  });

  const scenarios = scenariosData?.scenarios;

  const { handleSubmit, watch, setValue, reset, register } =
    useForm<AssignScenarioFormData>({
      defaultValues: {
        selectedUsersAndGroups: [],
        searchValue: "",
        setDueDate: true,
        dueDate: undefined,
        timezone: "PDT",
        includeCustomMessage: true,
        customMessage: includeCustomMessage ? "" : undefined,
        selectedScenarios: scenarioId ? [scenarioId] : [],
        scenarioAttemptMin: 0,
        scenarioAttemptMax: 0,
        scenarioRankMin: RoleplayRank.Unranked,
      },
    });

  const selectedUsersAndGroups = watch("selectedUsersAndGroups");
  // const includeCustomMessage = watch("includeCustomMessage");
  const dueDate = watch("dueDate");
  const scenarioAttemptMin = watch("scenarioAttemptMin");
  const scenarioAttemptMax = watch("scenarioAttemptMax");
  const scenarioRankMin = watch("scenarioRankMin");
  const isConfigAssignment = step === Step.CONFIG_ASSIGNMENT;
  const isChooseScenario = step === Step.CHOOSE_SCENARIO;
  // GraphQL mutation for bulk assignment
  const [bulkAssignScenarios, { loading: isSubmitting }] =
    useBulkAssignScenariosMutation({
      refetchQueries: [
        ScenarioAssignmentsDocument,
        ScenarioAssignmentsStatisticDocument,
      ],
      onCompleted: (data) => {
        if (data.bulkAssignScenarios?.ok) {
          if (!scenarioId) {
            navigate("/roleplays/assignments?collections=" + collectionId);
          } else {
            navigate(`/scenarios/${scenarioSlug}/assignments`);
          }
          handleClose();
        } else {
          setError("Could not assign scenarios. Please try again.");
        }
      },
      onError: (_) => {
        setError("Could not assign scenarios. Please try again.");
      },
    });

  // Query for existing assignments (for the "Current Assignments" section)
  const { data: assignmentsData } = useScenarioAssignmentsQuery({
    variables: {
      workspaceId: currentWorkspace?.id || "",
      ...(collectionId && { collectionIds: [collectionId] }),
      ...(scenarioId && { scenarioIds: [scenarioId] }),
      pageSize: 4, // Show first 4 assignments
      page: 1,
    },
    fetchPolicy: "network-only",
    skip: !currentWorkspace?.id || !open,
  });
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const maxDate = addYears(new Date(), 1);
  const minDate = new Date();

  const currentScenarios = scenarios?.data || [];

  useEffect(() => {
    if (selectedUsersAndGroups.length > 0) {
      setValidationError(null);
      setStep(Step.CONFIG_ASSIGNMENT);
    }
  }, [selectedUsersAndGroups]);

  const handleFormSubmit = async (data: AssignScenarioFormData) => {
    if (!currentWorkspace?.id) return;

    setValidationError(null);

    try {
      // Separate user and group IDs properly
      const userIds = data.selectedUsersAndGroups
        .filter((tag) => tag.type === "person")
        .map((tag) => tag.id);
      const groupIds = data.selectedUsersAndGroups
        .filter((tag) => tag.type === "group")
        .map((tag) => tag.id);

      if (scenarioId || selectedScenarios.length > 0) {
        await bulkAssignScenarios({
          variables: {
            scenarioIds: scenarioId ? [scenarioId] : selectedScenarios,
            userIds: userIds.length > 0 ? userIds : undefined,
            groupIds: groupIds.length > 0 ? groupIds : undefined,
            dueDate:
              data.setDueDate && data.dueDate
                ? data.dueDate.toISOString()
                : undefined,
            customMessage:
              data.includeCustomMessage && data.customMessage
                ? data.customMessage
                : undefined,
            scenarioAttemptMin:
              data.scenarioAttemptMin === 0
                ? undefined
                : data.scenarioAttemptMin,
            scenarioAttemptMax:
              data.scenarioAttemptMax === 0
                ? undefined
                : data.scenarioAttemptMax,
            scenarioRankMin:
              data.scenarioRankMin === RoleplayRank.Unranked
                ? undefined
                : data.scenarioRankMin,
          },
        });
      }
    } catch (_) {
      setValidationError("Failed to assign scenarios. Please try again.");
    }
  };

  const handleClose = () => {
    reset();
    setSelectedScenarios([]);
    setStep(Step.SELECT_PEOPLE_AND_GROUPS);
    setPage(1);
    setValidationError(null);
    onOpenChange(false);
  };

  const handleNextFromAssignment = () => {
    const formData = watch();
    const validationErrors: string[] = [];

    // Validate people/groups are selected
    if (
      !formData.selectedUsersAndGroups ||
      formData.selectedUsersAndGroups.length === 0
    ) {
      validationErrors.push("Please select at least one person or group");
    }

    // Validate due date is set
    if (!formData.dueDate) {
      validationErrors.push("Due date is required");
    }

    if (validationErrors.length > 0) {
      setValidationError(validationErrors.join(". "));
      return;
    }

    setValidationError(null);
    if (collectionId) {
      setStep(Step.CHOOSE_SCENARIO);
    } else {
      handleSubmit(handleFormSubmit)();
    }
  };

  const toggleScenario = useCallback((scenarioId: string) => {
    setSelectedScenarios((prev) =>
      prev.includes(scenarioId)
        ? prev.filter((id) => id !== scenarioId)
        : [...prev, scenarioId],
    );
  }, []);

  // Create stable callbacks for each scenario to prevent re-renders
  const scenarioCallbacks = useMemo(() => {
    const callbacks: Record<string, () => void> = {};
    currentScenarios.forEach((scenario) => {
      callbacks[scenario?.id ?? ""] = () => toggleScenario(scenario?.id ?? "");
    });
    return callbacks;
  }, [currentScenarios, toggleScenario]);

  const getDialogTitle = () => {
    if (isChooseScenario) return "Choose Scenarios";
    return "Assign Scenario";
  };

  const getSubmitButtonText = () => {
    if (isChooseScenario) {
      const count = selectedScenarios.length;
      return count > 0
        ? `Assign ${count} Scenario${count > 1 ? "s" : ""}`
        : "Assign";
    }
    return collectionId ? "Next" : "Assign";
  };

  const isAssignDisabled = useMemo(() => {
    return (
      isSubmitting ||
      (step === Step.CHOOSE_SCENARIO && selectedScenarios.length === 0)
    );
  }, [isSubmitting, selectedScenarios]);

  const navigate = useNavigate();
  const handleSeeAllAssignments = () => {
    if (scenarioSlug) {
      navigate(`/scenarios/${scenarioSlug}/assignments`);
    } else {
      if (collectionId) {
        navigate(`/roleplays/assignments?collections=${collectionId}`);
      } else {
        navigate(`/roleplays/assignments`);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[550px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {(step === Step.CONFIG_ASSIGNMENT ||
              step === Step.CHOOSE_SCENARIO) && (
              <ArrowLeft
                className="w-4 h-4 cursor-pointer"
                onClick={() => {
                  setValidationError(null);
                  if (step === Step.CHOOSE_SCENARIO) {
                    setStep(Step.CONFIG_ASSIGNMENT);
                  } else {
                    setValue("selectedUsersAndGroups", []);
                    setStep(Step.SELECT_PEOPLE_AND_GROUPS);
                  }
                }}
              />
            )}
            <DialogTitle>{getDialogTitle()}</DialogTitle>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogInner>
            {error && (
              <Callout variant="error" className="mb-4">
                {error}
              </Callout>
            )}
            {validationError && (
              <Callout variant="error" className="mb-4">
                {validationError}
              </Callout>
            )}

            {/* Scenario Selection Step */}
            {isChooseScenario && (
              <div className="space-y-4">
                <div className="space-y-3">
                  {currentScenarios.map((scenario) => (
                    <SimpleScenarioCard
                      key={scenario?.id ?? ""}
                      scenario={scenario as Scenario}
                      selected={selectedScenarios.includes(scenario?.id ?? "")}
                      onToggle={scenarioCallbacks[scenario?.id ?? ""]}
                      showShortDesc={false}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-gray-500">
                    {page} of {scenarios?.totalPages ?? 0}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage((prev) =>
                          Math.min(scenarios?.totalPages ?? 0, prev + 1),
                        )
                      }
                      disabled={page === scenarios?.totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Action Buttons for Scenario Selection */}
                <div className="flex justify-end gap-4 pt-6">
                  <Button
                    variant="minimal"
                    onClick={handleClose}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-[40px]"
                    type="button"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className={`bg-blue-600 hover:bg-blue-700 text-white px-6 h-[40px] ${
                      selectedScenarios.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={selectedScenarios.length === 0}
                    onClick={() => {
                      if (selectedScenarios.length === 0) {
                        setValidationError(
                          "Please select at least one scenario",
                        );
                        return;
                      }
                      setValidationError(null);
                      handleSubmit(handleFormSubmit)();
                    }}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    ) : (
                      getSubmitButtonText()
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* People & Groups Selection - Only show in first two steps */}
            {!isChooseScenario && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  People & Groups
                </Label>

                <PeopleAndGroupInput
                  onChange={(users, groups) => {
                    const userTags = users.map((user) => ({
                      id: user.id ?? "",
                      name: user.name ?? "",
                      type: "person" as const,
                    }));

                    const groupTags = groups.map((group) => ({
                      id: group.id ?? "",
                      name: group.name ?? "",
                      type: "group" as const,
                    }));

                    setValue("selectedUsersAndGroups", [
                      ...userTags,
                      ...groupTags,
                    ]);
                  }}
                />
              </div>
            )}

            {/* Current Assignments - Only show when no selections */}
            {!isConfigAssignment && !isChooseScenario && (
              <div className="bg-gray-50 rounded-md p-0">
                <div className="flex items-center justify-between pt-6 pb-2">
                  <div className="font-semibold text-base">
                    Current Assignments (
                    {assignmentsData?.scenarioAssignments?.total ?? 0})
                  </div>
                  <Button
                    onClick={handleSeeAllAssignments}
                    variant="outline"
                    size="sm"
                    className="text-gray-600 border-gray-200 bg-gray-100 h-[40px]"
                    type="button"
                  >
                    See All
                  </Button>
                </div>
                <div className="pb-4">
                  {assignmentsData?.scenarioAssignments?.data?.map(
                    (assignment) =>
                      assignment && (
                        <AssignmentCard
                          key={assignment.id}
                          assignment={assignment}
                        />
                      ),
                  )}
                </div>
              </div>
            )}

            {/* Due Date Section - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && (
              <div className="space-y-4 pt-6">
                <div className="space-y-4">
                  <Label className="text-base font-semibold mb-1">
                    Due Date
                  </Label>
                  <div className="flex gap-3 w-full">
                    <div style={{ position: "relative", width: "100%" }}>
                      <CalendarDays
                        style={{
                          position: "absolute",
                          top: 12,
                          left: 10,
                          zIndex: 99,
                          color: SUBTEXT_COLOR,
                        }}
                        strokeWidth={1.5}
                      />
                      <DateTimeInputV2
                        onChange={(date) => {
                          setValue(
                            "dueDate",
                            date ? new Date(date) : undefined,
                          );
                        }}
                        value={dueDate?.toISOString() ?? null}
                        timezone={timezone}
                        onChangeTimezone={setTimezone}
                        maxDateTime={maxDate}
                        minDateTime={minDate}
                        containerStyles={{
                          height: "50px",
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Completion Requirements Section - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && (
              <CompletionRequirements
                className="pt-6"
                scenarioRankMin={scenarioRankMin}
                scenarioAttemptMin={scenarioAttemptMin}
                scenarioAttemptMax={scenarioAttemptMax}
                onRankMinChange={(value) =>
                  setValue("scenarioRankMin", value as RoleplayRank)
                }
                onAttemptMinChange={(value) =>
                  setValue("scenarioAttemptMin", value)
                }
                onAttemptMaxChange={(value) =>
                  setValue("scenarioAttemptMax", value)
                }
                showTitle={true}
              />
            )}

            {/* Custom Message Section - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && !isChooseScenario && (
              <div className="space-y-4 pt-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={includeCustomMessage}
                    onCheckedChange={(checked) =>
                      setIncludeCustomMessage(checked === true)
                    }
                  />
                  <Label className="text-sm text-gray-700 font-normal">
                    Include custom message
                  </Label>
                </div>

                {includeCustomMessage && (
                  <Textarea
                    {...register("customMessage")}
                    placeholder="Message..."
                    className="min-h-[96px] text-sm"
                  />
                )}
              </div>
            )}

            {/* Action Buttons - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && (
              <div className="space-y-4">
                <div className="flex justify-end gap-4 pt-6">
                  <Button
                    variant="minimal"
                    onClick={handleClose}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-[40px]"
                    type="button"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNextFromAssignment}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-[40px]"
                    type="button"
                    disabled={isAssignDisabled}
                  >
                    {isSubmitting && !collectionId
                      ? "Assigning..."
                      : getSubmitButtonText()}
                  </Button>
                </div>
              </div>
            )}

            {/* Done Button - Only show when no selections and not choosing scenarios */}
            {!isConfigAssignment && !isChooseScenario && (
              <div className="flex justify-end">
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-[40px]"
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleClose}
                >
                  Done
                </Button>
              </div>
            )}
          </DialogInner>
        </form>
      </DialogContent>
    </Dialog>
  );
}
